import type { CellarLocation, CellarPlaceRecord } from "../types";

interface LocationSelectProps {
  locations: readonly CellarLocation[];
  value: string;
  onChange: (value: string) => void;
  emptyLabel: string;
  emptyValue?: string;
  placeholder?: string;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  places?: readonly CellarPlaceRecord[];
  includePlaceOptions?: boolean;
}

export function LocationSelect({
  locations,
  value,
  onChange,
  emptyLabel,
  emptyValue = "",
  placeholder,
  id,
  required,
  disabled,
  places = [],
  includePlaceOptions = false,
}: LocationSelectProps) {
  const placeGroups = places.map((place) => ({
    id: place.id,
    label: place.nom,
    values: locations.filter((location) => location.lieu_id === place.id),
  }));
  const knownIds = new Set(places.map((place) => place.id));
  const legacyNames = [...new Set(
    locations
      .filter((location) => !location.lieu_id || !knownIds.has(location.lieu_id))
      .map((location) => location.lieu)
      .filter((place): place is string => Boolean(place)),
  )];
  const groups = [
    ...placeGroups,
    ...legacyNames.map((place) => ({
      id: null,
      label: place,
      values: locations.filter(
        (location) =>
          (!location.lieu_id || !knownIds.has(location.lieu_id)) &&
          location.lieu === place,
      ),
    })),
    {
      id: null,
      label: "Lieu à préciser",
      values: locations.filter((location) => location.lieu == null),
    },
  ].filter((group) => group.values.length > 0 || (includePlaceOptions && group.id));

  return (
    <select
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      required={required}
      disabled={disabled}
    >
      {placeholder ? <option value="" disabled>{placeholder}</option> : null}
      <option value={emptyValue}>{emptyLabel}</option>
      {groups.map((group) => (
        <optgroup key={group.id ?? group.label} label={group.label}>
          {includePlaceOptions && group.id ? (
            <option value={`lieu:${group.id}`}>{group.label} — tous</option>
          ) : null}
          {group.values.map((location) => (
            <option key={location.id} value={location.id}>
              {location.libelle}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
